import SendIcon from "@mui/icons-material/Send";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import EntityMember from "_shared/entity-members/entity-member";
import { CanceledError } from "axios";
import {
  getIndividualByUserId,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { marketplaceTeamsApi } from "marketplace/teams/_api/marketplace-team-api";
import Team from "marketplace/teams/team";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import UserInformation from "users/information/user-information";
import { useSession } from "users/__session/session-context";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { useCreateProposalMutation } from "work/proposals/_api/proposal-api";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import {
  useLazyGetProposalCollaborationQuery,
  useSubmitProposalCollaborationDraftMutation,
} from "work/proposals/negotiation/proposal-collaboration-api";
import { buildSubmitProposalDraftCommand } from "work/proposals/negotiation/proposal-command-builder";
import {
  proposalCollaborationStorageScopeFromSession,
  proposalCollaborationViewerScope,
} from "work/proposals/negotiation/proposal-collaboration-local-store";
import { proposalEnvelopeReceived } from "work/proposals/negotiation/proposal-collaboration-redux-slice";
import Proposal from "work/proposals/proposal";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";
import ClientSelector from "work/work-agreements/teams/client-team/_views/client-selector";
import SelectedVendorTeam from "work/work-agreements/teams/vendor-team/selection/selected-vendor-team/selected-vendor-team";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

const Content = styled("section")(({ theme }) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  // The Client Representative's shrunk label floats above its input and so above this section's own
  // top edge, where the dialog's scroll container shaves it. Nothing here was labelled before, which
  // is why the row of fields needs the room only now.
  paddingTop: theme.spacing(1.5),
  rowGap: theme.spacing(2),
}));
const ProposalActions = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  flexGrow: 1,
  flexWrap: "nowrap",
  columnGap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));
const ProposalActionButton = styled(LoadingButton)(() => ({
  width: "100%",
  whiteSpace: "nowrap",
  "&.Mui-disabled": {
    color: "rgba(0, 0, 0, 0.26) !important",
    backgroundColor: "rgba(0, 0, 0, 0.12) !important",
  },
}));

type ProposalRequestDraftDialogProps = {
  onProposalSubmitted?: (proposal: Proposal) => void;
  onCancel: () => void;
  searchParams?: URLSearchParams;
};

export default function ProposalRequestDraftDialog(props: Readonly<ProposalRequestDraftDialogProps>) {
  const { onProposalSubmitted, onCancel, searchParams } = props;

  // #region Hooks
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const effectiveSearchParams = useMemo(
    () => searchParams ?? new URLSearchParams(globalThis.location.search),
    [searchParams],
  );
  const preSelectedLeader = useMemo(
    () => EntityMember.fromURLSearchParams(effectiveSearchParams),
    [effectiveSearchParams],
  );
  const preSelectedTeam = useMemo(
    () => SelectedVendorTeam.fromURLSearchParams(effectiveSearchParams),
    [effectiveSearchParams],
  );
  const viewerScope = useMemo(() => {
    const storageScope = proposalCollaborationStorageScopeFromSession(session);
    return storageScope ? proposalCollaborationViewerScope(storageScope) : undefined;
  }, [session]);

  // #region Queries
  const preselectedLeaderIndividual = useSelector(getIndividualByUserId(preSelectedLeader?.userId));
  useGetIndividualsByUserIdsQuery(
    { userIds: new Set(preSelectedLeader?.userId ? [preSelectedLeader.userId.value] : []) },
    { skip: !preSelectedLeader?.userId || !!preselectedLeaderIndividual },
  );
  const [createProposalQuery] = useCreateProposalMutation();
  const [loadProposalCollaboration] = useLazyGetProposalCollaborationQuery();
  const [submitProposalCollaborationDraft] = useSubmitProposalCollaborationDraftMutation();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [descriptionValue, setDescriptionValue] = React.useState<string>("");
  const [proposalBuilder, setProposalBuilder] = React.useState<ProposalBuilder>(() => new ProposalBuilder({ session }));
  const [clientSelectorAnchor, setClientSelectorAnchor] = React.useState<HTMLElement>();

  /**
   * The requester stands as the client on their own request, but only when they can sign for their
   * entity: `ProposalRequest.ValidateNegotiatorAsync` asserts signing authority on the negotiator and
   * the submit failed with nothing but "Failed to submit proposal" otherwise (issue #3786). A member
   * who cannot sign nominates a colleague through the picker below instead.
   */
  const defaultClientLeader = useMemo(
    () => (session.user?.isCompanyManager ? session.user.toEntityMember() : undefined),
    [session.user],
  );

  const clientLeaderUserId = (proposalBuilder.currentSpec.workAgreement.clientTeam as ClientTeam)?.leader?.userId;
  const cachedClientLeaderIndividual = useSelector(getIndividualByUserId(clientLeaderUserId));
  const clientLeaderIndividualQuery = useGetIndividualsByUserIdsQuery(
    { userIds: new Set(clientLeaderUserId ? [clientLeaderUserId.value] : []) },
    { skip: !clientLeaderUserId || !!cachedClientLeaderIndividual },
  );
  const queriedClientLeaderIndividual = Individual.fromJSON(
    clientLeaderIndividualQuery.data?.find((individual) => individual.userId === clientLeaderUserId?.value),
  );
  const clientLeaderIndividual = cachedClientLeaderIndividual ?? queriedClientLeaderIndividual;
  const clientLeaderIsLoading = Boolean(
    clientLeaderUserId &&
      !clientLeaderIndividual &&
      (clientLeaderIndividualQuery.isLoading || clientLeaderIndividualQuery.isFetching),
  );

  // #endregion

  const buildProposalWithPreselectedLeader = useCallback((builder: ProposalBuilder, leader: EntityMember) => {
    // TODO: Validate that leader has access
    return builder
      .setClient(defaultClientLeader)
      .setName(new ProjectName("New Proposal"))
      .setVendorTeam(new VendorTeam({ leader: leader, members: [] }));
  }, [defaultClientLeader]);

  const buildProposalWithPreselectedTeam = useCallback(async (
    builder: ProposalBuilder,
    preSelectedTeam: SelectedVendorTeam,
  ) => {
    try {
      const teamJSON = await dispatch(marketplaceTeamsApi.endpoints.getTeamById.initiate({ teamId: preSelectedTeam.targetTeamId.value })).unwrap();
      if (!teamJSON) throw new Error("Failed to load team");
      const team = Team.fromJSON(teamJSON);
      if (!team) throw new Error("Failed to load team");
      if (!team.leader?.userId || !team.leader?.legalEntityId) return builder;

      return builder
        .setClient(defaultClientLeader)
        .setName(new ProjectName("New Proposal"))
        .setVendorTeam(team.toWorkTeam());
    } catch (error) {
      if (error instanceof CanceledError) return builder;
      console.error(error);
      return builder;
    }
  }, [defaultClientLeader, dispatch]);

  const initProposal = useCallback(async (isActive: () => boolean = () => true) => {
    try {
      setIsLoading(true);
      let nextProposalBuilder = new ProposalBuilder({ session });

      if (preSelectedLeader) {
        nextProposalBuilder = buildProposalWithPreselectedLeader(nextProposalBuilder, preSelectedLeader);
      }
      if (preSelectedTeam) {
        nextProposalBuilder = await buildProposalWithPreselectedTeam(nextProposalBuilder, preSelectedTeam);
      }
      if (isActive()) {
        setProposalBuilder(nextProposalBuilder);
      }
    } catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
    } finally {
      if (isActive()) {
        setIsLoading(false);
      }
    }
  }, [
    buildProposalWithPreselectedLeader,
    buildProposalWithPreselectedTeam,
    preSelectedLeader,
    preSelectedTeam,
    session
  ]);

  useEffect(() => {
    let active = true;
    queueMicrotask(() => {
      if (!active) return;
      void initProposal(() => active);
    });
    return () => {
      active = false;
    };
  }, [initProposal]);

  async function handleSubmitClicked() {
    try {
      setIsSubmitting(true);
      const issues = ProposalIssues.fromBuilder(proposalBuilder);
      if (!issues.canSubmit) return;
      const draftProposal = proposalBuilder.buildNewDraft();
      const createdProposalJSON = await createProposalQuery({ proposal: draftProposal, session }).unwrap();
      if (!createdProposalJSON.id) throw new Error("Created proposal is missing an ID");
      if (!viewerScope) throw new Error("Proposal collaboration viewer is not ready for submission");

      const submissionEnvelope = await loadProposalCollaboration(
        { proposalId: createdProposalJSON.id, viewerScope },
        false,
      ).unwrap();
      const submittedEnvelope = await submitProposalCollaborationDraft({
        negotiationId: submissionEnvelope.negotiationId,
        viewerScope,
        command: buildSubmitProposalDraftCommand({ envelope: submissionEnvelope }),
      }).unwrap();
      dispatch(proposalEnvelopeReceived({ envelope: submittedEnvelope, viewerScope }));
      const submittedProposal = Proposal.fromJSON({
        json: submittedEnvelope.projection,
        currentUser: session.user!,
      });
      if (!submittedProposal) throw new Error("Failed to deserialize submitted proposal");

      onProposalSubmitted?.(submittedProposal);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to submit proposal", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleSelectClientClicked(event: React.MouseEvent<HTMLDivElement>) {
    const inputElements = event.currentTarget.getElementsByTagName("input");
    setClientSelectorAnchor(inputElements.length > 0 ? inputElements[0] : event.currentTarget);
  }

  function handleClientUpdated(userInfo?: UserInformation) {
    const userId = userInfo?.id;
    const legalEntityId = userInfo?.legalEntityId;
    if (!userId || !legalEntityId) {
      setProposalBuilder((builder) => builder.setClient());
      return;
    }

    setProposalBuilder((builder) => builder.setClient(new EntityMember({ legalEntityId, userId })));
  }

  function handleDescriptionChanged(event: React.ChangeEvent<HTMLInputElement>) {
    setDescriptionValue(event.target.value);

    setProposalBuilder((builder) => builder.setDescription(new ProjectDescription(event.target.value)));
  }

  const issues = useMemo(() => ProposalIssues.fromBuilder(proposalBuilder), [proposalBuilder]);
  const clientIssues = issues.entries
    .filter((entry) => entry.field.isEqualTo(ProposalField.Client))
    .map((entry) => entry.description);

  let clientValue = "";
  if (clientLeaderIndividual) clientValue = clientLeaderIndividual.fullName;
  else if (clientLeaderIsLoading) clientValue = "Loading...";

  return (
    <>
      <Content>
        {isLoading && <Loader />}
        {!isLoading && (
          <>
            <TextField
              fullWidth
              required
              label="Client Representative"
              autoComplete="off"
              value={clientValue}
              error={clientIssues.length > 0}
              helperText={clientIssues.join("\n")}
              disabled={clientSelectorAnchor !== undefined}
              onClick={handleSelectClientClicked}
              slotProps={{
                htmlInput: { readOnly: true },
                inputLabel: { shrink: Boolean(clientValue) },
              }}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              placeholder="Enter a brief description of what you need"
              value={descriptionValue}
              onChange={handleDescriptionChanged}
              onKeyDown={(event) => {
                if (event.key === KeyboardKey.Enter) {
                  void handleSubmitClicked();
                }
              }}
            />
          </>
        )}
      </Content>
      <ProposalActions>
        <ProposalActionButton
          variant="contained"
          color="primary"
          startIcon={<SendIcon />}
          loading={isSubmitting}
          disabled={isLoading || !issues.canSubmit}
          onClick={handleSubmitClicked}
        >
          Submit
        </ProposalActionButton>
        <ProposalActionButton
          variant="contained"
          loading={false}
          disabled={isLoading}
          onClick={onCancel}
        >
          Cancel
        </ProposalActionButton>
      </ProposalActions>
      <ClientSelector
        clientUserId={clientLeaderUserId}
        popoverAnchor={clientSelectorAnchor}
        onPopoverClose={() => setClientSelectorAnchor(undefined)}
        onClientUpdated={handleClientUpdated}
      />
    </>
  );
}
